/**
 * Design Wizard — guided new-game bootstrap (Quarry-inspired). Turns a pitch +
 * a small game profile into a first-draft design where Systems, Content, and
 * Art Direction are populated as modular ITEMS (the card model), and Overview
 * as prose.
 *
 * Steps: Pitch → Dive deeper (+AI) → Name (+Suggest) → Profile (genre/players/
 * input) → Art style (+AI) → Generate/review/apply.
 *
 * Drafting reuses the structured-markdown contract: item-mode sections use
 * ITEM_SECTION_OUTPUT_INSTRUCTIONS → parseItems; Overview keeps the BODY path.
 * The profile + ITEM_MODEL_GUIDE ride along in every prompt so the AI stays on
 * the same page as the editor.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { DesignProject, GenreKey, PlayerSpec } from '@volley/crucible-ipc-types';
import { Sparkles, X } from 'lucide-react';
import { inferGenreKey } from '@volley/crucible-ipc-types/genre-fallback';
import type { CrucibleClient } from '@volley/crucible-client';
import { useCrucibleClient } from '../../transport/context.js';
import { RichMarkdown } from './RichMarkdown';
import { Tip } from './Tip';
import {
  aiChat,
  readDesignSystem,
  readLens,
  renameProject,
  suggestGameName,
  writeProjectProfile,
  writeSection,
} from './anvil-api';
import {
  WIZARD_GUIDE,
  ITEM_MODEL_GUIDE,
  designSystemContextBlock,
  profileContextBlock,
  wizardSectionGuidance,
} from './prompts';
import {
  SECTION_OUTPUT_INSTRUCTIONS,
  ITEM_SECTION_OUTPUT_INSTRUCTIONS,
  parseSectionResponse,
} from './lib/section-response';
import { parseItems, serializeItems, itemModeFor, withGameLoopFirst } from './lib/section-items';
import { INPUT_METHODS, GENRE_OPTIONS, MAX_PLAYERS } from './lib/profile-options';
import { buildHiddenReferenceContext, formatReferenceUsage } from './lib/reference-selection';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Chip } from '../../components/ui/chip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

export interface WizardDialogProps {
  gameId: string;
  project: DesignProject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lensIds?: { id: string; kind: 'agent' | 'skill' }[];
  /** Sections already carry real content → warn that drafting rewrites them. */
  hasExistingContent?: boolean;
  onApplied?: () => void;
}

interface Draft {
  sectionId: string;
  filename: string;
  title: string;
  doc: 'gdd' | 'prd';
  isItems: boolean;
  body: string; // section body markdown (no title heading)
  drafting: boolean;
  error?: string;
  accepted: boolean;
}

type Step = 'pitch' | 'deeper' | 'name' | 'profile' | 'art' | 'generate';
const FORM_STEPS: Step[] = ['pitch', 'deeper', 'name', 'profile', 'art'];
const STEP_TITLES: Record<Step, string> = {
  pitch: 'Pitch',
  deeper: 'Dive deeper',
  name: 'Name your game',
  profile: 'Genre, players & input',
  art: 'Art style',
  generate: 'Review & apply',
};

async function buildLensBlock(
  client: CrucibleClient,
  lensIds: { id: string; kind: 'agent' | 'skill' }[],
): Promise<string> {
  if (lensIds.length === 0) return '';
  const parts: string[] = ['\n\n# Active lenses\n'];
  for (const { id, kind } of lensIds) {
    try {
      const full = await readLens(client, id, kind);
      parts.push(`\n## Lens: ${full.name}\n${full.description}\n\n${full.body}\n`);
    } catch {
      /* skip */
    }
  }
  return parts.join('');
}

export function WizardDialog({
  gameId,
  project,
  open,
  onOpenChange,
  lensIds = [],
  hasExistingContent = false,
  onApplied,
}: WizardDialogProps): ReactElement {
  const client = useCrucibleClient();
  const [step, setStep] = useState<Step>('pitch');
  const [pitch, setPitch] = useState(project.pitch ?? '');
  const [deeper, setDeeper] = useState('');
  const [name, setName] = useState(project.name ?? '');
  const [genre, setGenre] = useState<GenreKey>(project.profile?.genre ?? 'default');
  const [players, setPlayers] = useState<PlayerSpec>(
    project.profile?.players ?? { mode: 'multiplayer', max: 4 },
  );
  const [inputStyles, setInputStyles] = useState<string[]>(project.profile?.inputStyles ?? []);
  const [artStyle, setArtStyle] = useState(project.profile?.artStyle ?? '');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [referenceNames, setReferenceNames] = useState<string[]>([]);
  // Whether the user has explicitly picked a genre (so we stop auto-guessing).
  const [genreTouched, setGenreTouched] = useState(Boolean(project.profile?.genre));
  // crucible#2968 — the create-time design agent's locked identity, when one
  // was generated for this game. Fetched once per open; `''` (never fetched)
  // is distinguished from a resolved-but-empty block so a slow/failed read
  // doesn't race a click of "Suggest" — both callers just fold in whatever
  // string is present at call time (empty string is a harmless no-op).
  const [designSystemBlock, setDesignSystemBlock] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Clear synchronously BEFORE the fetch — otherwise a game switch (or a
    // reopen) leaves the PREVIOUS game's locked block sitting in state for
    // the duration of the new read (or forever, if that read fails), and
    // `aiSuggestArt`/`generateDrafts` would inject the wrong game's identity
    // in the meantime. Bugbot finding on crucible#2970.
    setDesignSystemBlock('');
    void readDesignSystem(client, gameId)
      .then((record) => {
        if (!cancelled) setDesignSystemBlock(designSystemContextBlock(record));
      })
      .catch(() => {
        // Best-effort: no design system context is exactly the pre-#2968
        // behaviour (pitch-only drafting), never a blocking error.
      });
    return () => {
      cancelled = true;
    };
  }, [open, gameId, client]);

  const docLabel = project.projectType.toUpperCase();

  // Cheap deterministic genre guess from pitch/name until the user changes it.
  useEffect(() => {
    if (genreTouched) return;
    const guess = inferGenreKey(name, `${pitch} ${deeper}`);
    setGenre(guess);
  }, [name, pitch, deeper, genreTouched]);

  const profile = useMemo(
    () => ({ genre, players, inputStyles, artStyle }),
    [genre, players, inputStyles, artStyle],
  );

  // ---- AI assist buttons ----------------------------------------------------

  const aiElaborate = useCallback(async () => {
    if (!pitch.trim()) return;
    setBusy(true);
    setError(null);
    setStatus('Drafting a deeper description…');
    try {
      const resp = await aiChat(client, {
        gameId,
        system: `${WIZARD_GUIDE}\n\nTask: Expand the pitch into a richer concept description (1–2 short paragraphs): the core loop, the player fantasy, and what makes it fun on a TV+phone, host-led setup. Output ONLY the prose — no headings, no preamble.`,
        messages: [{ role: 'user', content: `Pitch: ${pitch}` }],
        maxTokens: 1024,
      });
      setDeeper(resp.text.trim());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [pitch, gameId, client]);

  const aiSuggestName = useCallback(async () => {
    setBusy(true);
    setError(null);
    setStatus('Thinking of a name…');
    try {
      const concept = [pitch, deeper].filter(Boolean).join('\n\n');
      const { displayName } = await suggestGameName(client, concept || pitch);
      if (displayName?.trim()) setName(displayName.trim());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [pitch, deeper, client]);

  const aiSuggestArt = useCallback(async () => {
    setBusy(true);
    setError(null);
    setStatus('Sketching an art direction…');
    try {
      const resp = await aiChat(client, {
        gameId,
        system: `${WIZARD_GUIDE}${profileContextBlock(profile)}${designSystemBlock}\n\nTask: Propose a concise art-style direction (palette, mood, character/object treatment, UI tone) for this game in 1–3 sentences. Output ONLY the description.`,
        messages: [
          { role: 'user', content: `Game: ${name || project.name}\nPitch: ${pitch}\n${deeper}` },
        ],
        maxTokens: 512,
      });
      setArtStyle(resp.text.trim());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [gameId, profile, name, project.name, pitch, deeper, designSystemBlock, client]);

  // ---- Generate -------------------------------------------------------------

  const generateDrafts = useCallback(async () => {
    setError(null);
    setStep('generate');
    const lensBlock = await buildLensBlock(client, lensIds);
    const ref = await buildHiddenReferenceContext(client, {
      gameId,
      intent: `${pitch}\n${deeper}`,
    });
    setReferenceNames(ref.names);

    const sharedSystem =
      WIZARD_GUIDE +
      '\n\n' +
      ITEM_MODEL_GUIDE +
      lensBlock +
      ref.block +
      profileContextBlock(profile) +
      designSystemBlock +
      `\n\n# Project context\nGame: ${name || project.name}\nDocument type: ${docLabel}\nPitch: ${pitch}` +
      (deeper.trim() ? `\n\n# Concept detail\n${deeper}` : '');

    const ordered = [...project.sections].sort((a, b) => a.order - b.order);
    setDrafts(
      ordered.map((s) => ({
        sectionId: s.id,
        filename: s.filename,
        title: s.title,
        doc: s.doc,
        isItems: itemModeFor(s),
        body: '',
        drafting: true,
        accepted: true,
      })),
    );

    await Promise.allSettled(
      ordered.map(async (sec) => {
        const items = itemModeFor(sec);
        const outputInstructions = items
          ? ITEM_SECTION_OUTPUT_INSTRUCTIONS
          : SECTION_OUTPUT_INSTRUCTIONS;
        const sectionSystem = `${sharedSystem}\n\n# Task\nDraft the "${sec.title}" section of the ${sec.doc.toUpperCase()}${
          items ? ' as modular ITEMS' : ''
        }.\n\n## Section guidance\n${wizardSectionGuidance(sec.id)}\n\nStay literal to the pitch, concept detail, and game profile above.\n\n${outputInstructions}`;
        try {
          const resp = await aiChat(client, {
            gameId,
            system: sectionSystem,
            messages: [{ role: 'user', content: `Draft the ${sec.title} section now.` }],
            maxTokens: 8192,
          });
          let body: string;
          if (items) {
            const parsed = parseItems(resp.text.trim());
            if (parsed.items.length === 0)
              throw new Error(`No items returned (first 200: ${resp.text.slice(0, 200)})`);
            // Systems always leads with the Game Loop; pin it if the model
            // emitted it out of order (no-op for other item sections).
            body = serializeItems({
              preamble: '',
              items: withGameLoopFirst(parsed.items),
            }).trimEnd();
          } else {
            const parsed = parseSectionResponse(resp.text);
            if (!parsed.body)
              throw new Error(`No BODY returned (first 200: ${resp.text.slice(0, 200)})`);
            body = parsed.body;
          }
          setDrafts((cur) =>
            cur.map((d) => (d.sectionId === sec.id ? { ...d, body, drafting: false } : d)),
          );
        } catch (e: unknown) {
          setDrafts((cur) =>
            cur.map((d) =>
              d.sectionId === sec.id
                ? {
                    ...d,
                    drafting: false,
                    accepted: false,
                    error: e instanceof Error ? e.message : String(e),
                  }
                : d,
            ),
          );
        }
      }),
    );
  }, [
    lensIds,
    gameId,
    pitch,
    deeper,
    profile,
    name,
    project.name,
    project.sections,
    docLabel,
    designSystemBlock,
    client,
  ]);

  const apply = useCallback(async () => {
    setStatus('Writing design…');
    setError(null);
    try {
      for (const d of drafts.filter((x) => x.accepted && x.body)) {
        await writeSection(client, gameId, d.filename, `# ${d.title}\n\n${d.body.trim()}\n`);
      }
      await writeProjectProfile(client, gameId, profile);
      if (name.trim() && name.trim() !== project.name) {
        await renameProject(client, gameId, name.trim());
      }
      onApplied?.();
      onOpenChange(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [drafts, gameId, profile, name, project.name, onApplied, onOpenChange, client]);

  // ---- Step navigation ------------------------------------------------------

  const stepIndex = FORM_STEPS.indexOf(step);
  const goNext = (): void => {
    if (step === 'art') {
      void generateDrafts();
      return;
    }
    const i = FORM_STEPS.indexOf(step);
    if (i >= 0 && i < FORM_STEPS.length - 1) setStep(FORM_STEPS[i + 1]);
  };
  const goBack = (): void => {
    if (step === 'generate') {
      setStep('art');
      return;
    }
    const i = FORM_STEPS.indexOf(step);
    if (i > 0) setStep(FORM_STEPS[i - 1]);
  };
  const nextDisabled = busy || (step === 'pitch' && !pitch.trim());

  const toggleInput = (m: string): void =>
    setInputStyles((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="mb-3 flex-row items-center justify-between gap-3">
          <DialogTitle>Design wizard</DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            data-testid="design-modal-close"
            className="flex items-center gap-1"
          >
            <X size={13} />
            Cancel
          </Button>
        </DialogHeader>

        {/* Stepper */}
        {step !== 'generate' && (
          <div className="mb-3 flex items-center gap-1.5" data-testid="wizard-stepper">
            {FORM_STEPS.map((s, i) => (
              <span
                key={s}
                className={`h-1.5 flex-1 rounded-full ${
                  i <= stepIndex ? 'bg-crucible-accent' : 'bg-crucible-border'
                }`}
              />
            ))}
          </div>
        )}
        <p className="mb-2 text-sm font-semibold text-crucible-fg">{STEP_TITLES[step]}</p>

        {error && (
          <div
            className="mb-3 rounded border border-error/25 bg-error/12 p-2 text-xs text-error"
            data-testid="wizard-error"
          >
            {error}
          </div>
        )}

        {step === 'pitch' && (
          <div data-testid="wizard-pitch">
            {hasExistingContent && (
              <div
                className="mb-3 rounded border border-warning/25 bg-warning/12 p-2 text-xs text-warning"
                data-testid="wizard-rewrite-warning"
              >
                Generating rewrites the design sections — your current version is kept as a
                revision.
              </div>
            )}
            <p className="mb-2 text-sm text-crucible-muted">Start from your concept.</p>
            <Textarea
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              rows={5}
              placeholder="Describe the game…"
            />
          </div>
        )}

        {step === 'deeper' && (
          <div data-testid="wizard-deeper">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm text-crucible-muted">Flesh out the core loop and the fun.</p>
              <AiButton label="AI suggestion" busy={busy} onClick={() => void aiElaborate()} />
            </div>
            <textarea
              value={deeper}
              onChange={(e) => setDeeper(e.target.value)}
              rows={7}
              placeholder="What's the moment-to-moment loop? Why is it fun?"
              className="w-full resize-none rounded border border-crucible-border bg-transparent p-2 text-sm text-crucible-fg outline-none"
            />
          </div>
        )}

        {step === 'name' && (
          <div data-testid="wizard-name">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm text-crucible-muted">What's it called?</p>
              <AiButton label="Suggest" busy={busy} onClick={() => void aiSuggestName()} />
            </div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Game name"
              data-testid="wizard-name-input"
            />
          </div>
        )}

        {step === 'profile' && (
          <div data-testid="wizard-profile" className="space-y-4">
            <div>
              <label className="mb-1 block text-sm text-crucible-fg">Genre</label>
              <Select
                value={genre}
                data-testid="wizard-genre"
                onChange={(e) => {
                  setGenre(e.target.value as GenreKey);
                  setGenreTouched(true);
                }}
              >
                {GENRE_OPTIONS.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="mb-1 block text-sm text-crucible-fg">Players</label>
              <div className="flex items-center gap-2">
                <div className="flex rounded border border-crucible-border p-0.5">
                  {(['single', 'multiplayer'] as const).map((m) => (
                    <Chip
                      key={m}
                      variant="interactive"
                      size="sm"
                      data-testid={`wizard-players-${m}`}
                      onClick={() => setPlayers((p) => ({ ...p, mode: m }))}
                      className={
                        players.mode === m
                          ? 'border-crucible-accent bg-crucible-accent/20 text-crucible-fg hover:text-crucible-accent'
                          : ''
                      }
                    >
                      {m === 'single' ? 'Single player' : 'Multiplayer'}
                    </Chip>
                  ))}
                </div>
                {players.mode === 'multiplayer' && (
                  <label className="flex items-center gap-1 text-xs text-crucible-muted">
                    up to
                    <select
                      value={players.max}
                      data-testid="wizard-players-max"
                      onChange={(e) => setPlayers((p) => ({ ...p, max: Number(e.target.value) }))}
                      className="rounded border border-crucible-border bg-crucible-bg p-1 text-crucible-fg outline-none"
                    >
                      {Array.from({ length: MAX_PLAYERS - 1 }, (_, i) => i + 2).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    players
                  </label>
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm text-crucible-fg">Input style</label>
              <div className="flex flex-wrap gap-1.5" data-testid="wizard-inputs">
                {INPUT_METHODS.map((m) => {
                  const on = inputStyles.includes(m);
                  return (
                    <Chip
                      key={m}
                      variant="interactive"
                      size="sm"
                      onClick={() => toggleInput(m)}
                      className={
                        on
                          ? 'border-crucible-accent bg-crucible-accent/20 text-crucible-fg hover:text-crucible-accent'
                          : ''
                      }
                    >
                      {m}
                    </Chip>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 'art' && (
          <div data-testid="wizard-art">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm text-crucible-muted">How should it look and feel?</p>
              <AiButton label="AI assist" busy={busy} onClick={() => void aiSuggestArt()} />
            </div>
            <textarea
              value={artStyle}
              onChange={(e) => setArtStyle(e.target.value)}
              rows={5}
              placeholder="Palette, mood, character/UI treatment…"
              className="w-full resize-none rounded border border-crucible-border bg-transparent p-2 text-sm text-crucible-fg outline-none"
            />
          </div>
        )}

        {step === 'generate' && (
          <div data-testid="wizard-drafts">
            <p className="mb-2 text-xs text-crucible-muted">
              {drafts.filter((d) => d.accepted && d.body).length} of {drafts.length} sections
              selected. Systems, Content & Art Direction are drafted as items.
            </p>
            {referenceNames.length > 0 && (
              <p className="mb-2 text-xs text-crucible-muted" data-testid="wizard-references-used">
                {formatReferenceUsage(referenceNames)}
              </p>
            )}
            <ul className="space-y-3">
              {drafts.map((d) => (
                <li key={d.sectionId} className="rounded border border-crucible-border p-2">
                  <label className="flex items-center gap-2 text-sm text-crucible-fg">
                    <input
                      type="checkbox"
                      checked={d.accepted}
                      disabled={!d.body}
                      onChange={(e) =>
                        setDrafts((prev) =>
                          prev.map((x) =>
                            x.sectionId === d.sectionId ? { ...x, accepted: e.target.checked } : x,
                          ),
                        )
                      }
                    />
                    <span className="font-medium">{d.title}</span>
                    {d.isItems && <span className="f-meta text-crucible-accent-hover">items</span>}
                    {d.drafting && <span className="text-xs text-crucible-muted">drafting…</span>}
                    {d.error && <span className="text-xs text-error">failed</span>}
                  </label>
                  {d.body && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded bg-crucible-bg-elevated p-2 text-xs">
                      <RichMarkdown content={d.body} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer nav */}
        <DialogFooter className="justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            disabled={step === 'pitch' || (step === 'generate' && drafts.some((d) => d.drafting))}
          >
            Back
          </Button>
          {step === 'generate' ? (
            <Button
              size="sm"
              onClick={() => void apply()}
              disabled={drafts.some((d) => d.drafting) || !drafts.some((d) => d.accepted && d.body)}
              data-testid="wizard-apply"
            >
              Apply to design
            </Button>
          ) : (
            <Button size="sm" onClick={goNext} disabled={nextDisabled} data-testid="wizard-next">
              {busy ? status || 'Working…' : step === 'art' ? 'Generate design' : 'Continue'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AiButton({
  label,
  busy,
  onClick,
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <Tip label="Let the Co-Designer fill this in">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClick}
        disabled={busy}
        className="peer flex items-center gap-1"
      >
        <Sparkles size={12} /> {label}
      </Button>
    </Tip>
  );
}
